import React, { useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { mapValues, orderBy, uniqBy } from 'lodash'
import { nbPerPageLabel, SANDRE } from '../../constants/ReferencialConstants'
import { push } from '@lagunovsky/redux-react-router'
import { setLocalStorageJson } from '../../../utils/FormUtils'
import Select from '../../../components/forms/Select'
import { getDate } from '../../../utils/DateUtil'
import { getSandreLabel, searchAllCharacters } from '../../../utils/StringUtil'
import { getStatusInformation } from '../../util/StatusUtils'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import ReferencialAction from 'referencial/action/ReferencialAction'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import Input from 'components/forms/Input'
import { Button, Card, CardContent, Grid2, Stack } from '@mui/material'
import { hasValue } from 'utils/NumberUtil'
import { CardTitle } from 'components/card/NewCard'
import { CardTable } from 'components/datatable/NewTable'

const headers = ['field', 'code', 'name', 'mnemonique', 'displayStatus', 'reference']
const exportHeaders = ['field', 'code', 'name', 'mnemonique', 'status', 'reference', 'comment', 'creationDate', 'updateDate', 'author']

const LexiconsApp = () => {
    const {
        sandreCodes,
        referencialSandreCodes,
        referencialStatus,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
        referencialSandreCodes: store.ReferencialReducer.referencialSandreCodes,
        referencialStatus: store.ReferencialReducer.referencialStatus,
    }), shallowEqual)

    const [filter, setFilter] = useLocalStorage('LEXICON_FILTER', {})
    const [filterTmp, setFilterTmp] = useLocalStorage('LEXICON_FILTER', {})

    const dispatch = useDispatch()

    useEffect(() => {
        if (!referencialStatus.length) {
            dispatch(ReferencialAction.fetchReferencialStatus())
        }
    }, [])

    useTitle(() => [{
        title: i18n.referencials,
        href: '/referencial',
    }, {
        title: i18n.lexicons,
        href: '/referencial/lexicons',
    }], [])

    const filteredList = useMemo(() => filter.selectedField ? sandreCodes.filter(sandreCode => sandreCode.field === filter.selectedField) : sandreCodes, [filter.selectedField, sandreCodes])
    const refList = useMemo(() => filteredList.filter(sandreCode => !referencialSandreCodes.some(rsf => rsf.code && rsf.field === sandreCode.field)), [filteredList, referencialSandreCodes])
    const lexiconByStatus = useMemo(() => hasValue(filter.status) ? refList.filter(lex => lex.status === filter.status) : refList, [filter.status, refList])
    const dataLexicons = useMemo(() => orderBy(lexiconByStatus, ['field', 'code']).map(sandreCode => ({
        ...sandreCode,
        updateDate: getDate(sandreCode.updateDate),
        creationDate: getDate(sandreCode.date),
        code: `${sandreCode.code}`,
        displayStatus: sandreCode.status === 3 ? getStatusInformation('3') : getSandreLabel(sandreCodes, SANDRE.DISPOSITIFS_ETATS, sandreCode.status),
        status: getSandreLabel(sandreCodes, SANDRE.DISPOSITIFS_ETATS, sandreCode.status),
        headers: exportHeaders,
    })), [lexiconByStatus, sandreCodes])

    const data = useMemo(() => {
        return dataLexicons
            .filter((obj) => searchAllCharacters(headers.map((key) => obj[key]).join())
                .includes(searchAllCharacters(filter.searchValue || '')))
    }, [dataLexicons, filter])

    const colorData = useMemo(() => data.map(u => ({ ...mapValues(u, o => ({ value: o })), fieldLink: u.field, id: u.code })), [data])

    useActions(() => {
        return {
            new: () => dispatch(push('/referencial/lexicon/new/-1/dashboard')),
            referencialActions: {
                actions: {
                    export: {
                        data,
                        titleFile: i18n.lexicons,
                    },
                },
            },
        }
    }, [filter])

    useEffect(() => {
        dispatch(ReferencialAction.fetchSandreCodes())
        dispatch(ReferencialAction.fetchReferencialSandreCodes())
    }, [])

    const onFilterChange = (newFilter) => {
        setLocalStorageJson('LEXICON_FILTER', newFilter)
        setFilterTmp(newFilter)
    }

    return (
        <Stack margin={1} spacing={1}>
            <Card>
                <CardTitle title={i18n.criterias} color='bannerColor'/>
                <CardContent>
                    <Grid2 container spacing={1}>
                        <Grid2 size={4}>
                            <Select
                                label={i18n.lexicons}
                                value={filterTmp.selectedField}
                                options={orderBy(uniqBy(sandreCodes.filter(sandreCode => !referencialSandreCodes.some(rsf => rsf.code && rsf.field === sandreCode.field)), 'field'), 'field')}
                                onChange={v => onFilterChange({ ...filterTmp, selectedField: v })}
                                returnNull={true}
                                clearFunction
                                keyValue='field'
                                keyLabel='field'
                            />
                        </Grid2>
                        <Grid2 size={3}>
                            <Input
                                title={i18n.search}
                                value={filterTmp.searchValue}
                                onChange={v => setFilterTmp({ ...filterTmp, searchValue: v })}
                                onEnterKeyPress={() => setFilter({ ...filterTmp })}
                            />
                        </Grid2>
                        <Grid2 size={3}>
                            <Select
                                options={referencialStatus}
                                label={i18n.status}
                                onChange={v => setFilterTmp({ ...filterTmp, status: v })}
                                value={filterTmp.status}
                                nullLabel='&nbsp;'
                            />
                        </Grid2>
                        <Grid2 size={2} marginTop={2}>
                            <Button className='right' onClick={() => setFilter({ ...filterTmp })} variant='contained' color='primary'>
                                {i18n.search}
                            </Button>
                        </Grid2>
                    </Grid2>
                </CardContent>
            </Card>
            <CardTable
                title={i18n.nonSandreLexicons}
                rowsPerPageOptions={nbPerPageLabel}
                headersLabel={{ displayStatus: i18n.status }}
                rows={colorData}
                headers={headers}
                color='bannerColor'
                onClickRow={element => dispatch(push(`/referencial/lexicon/${element.fieldLink}/${element.id}/dashboard`))}
            />
        </Stack>
    )
}

export default LexiconsApp